/**
 * Decimal stock quantities in the backoffice (CHG-2026-09-stock-usage-and-price-twins, D1).
 *
 * Stock, usage quantities and variant shares hold up to 3 decimals in the article's base unit
 * (the server stores NUMERIC(12,3)). Input accepts the de-AT comma and the dot; output uses the
 * de-AT comma. Parsing goes from the validated decimal string straight to a number, so the value
 * sent as JSON is the shortest representation of what was typed ("0,03" → 0.03), never a float
 * with binary noise from arithmetic.
 */

/** Most decimals a stock quantity may carry. */
export const STOCK_DECIMALS = 3

/** Integer digits NUMERIC(12,3) leaves room for. */
const MAX_INTEGER_DIGITS = 9

export type StockQuantityError =
  | 'required'
  | 'invalid'
  | 'ambiguousDot'
  | 'tooManyDecimals'
  | 'notPositive'
  | 'tooLarge'

export type StockQuantityParse =
  | { ok: true; value: number }
  | { ok: false; error: StockQuantityError }

const DECIMAL_PATTERN = /^(\d+)(?:[.,](\d+))?$/

/**
 * Parses a positive quantity with at most 3 decimals: "1,5" (preferred), "0,5", "0.5", "12",
 * " 1,250 ".
 * Refuses empty input, garbage ("1,2,3", "1e3", "abc", "-1", ",5"), zero, more than 3 decimals and
 * values beyond NUMERIC(12,3).
 *
 * A dot followed by exactly three digits ("1.500", "1.000") is refused as `ambiguousDot`: in de-AT
 * the dot is the thousands separator, so "1.500" may mean 1500 as well as 1,5. The comma is
 * unambiguous ("1,500" is 1,5).
 */
export function parseStockQuantity(input: string): StockQuantityParse {
  const trimmed = input.trim()
  if (trimmed === '') return { ok: false, error: 'required' }
  const match = DECIMAL_PATTERN.exec(trimmed)
  if (!match) return { ok: false, error: 'invalid' }
  const [, integerPart, fraction = ''] = match
  if (trimmed.includes('.') && fraction.length === 3)
    return { ok: false, error: 'ambiguousDot' }
  if (fraction.length > STOCK_DECIMALS)
    return { ok: false, error: 'tooManyDecimals' }
  if (integerPart.replace(/^0+(?=\d)/, '').length > MAX_INTEGER_DIGITS)
    return { ok: false, error: 'tooLarge' }
  const value = Number(fraction ? `${integerPart}.${fraction}` : integerPart)
  if (!Number.isFinite(value)) return { ok: false, error: 'invalid' }
  if (value <= 0) return { ok: false, error: 'notPositive' }
  return { ok: true, value }
}

/**
 * The quantity one sale books for a part used through a share: `round(qty × share, 3)` — the
 * server's `StockLineResolver.perUnit`. Computed in thousandths so 0.03 × 0.5 gives 0.015, not
 * 0.015000000000000001.
 */
export function perUnitQuantity(qty: number, share: number): number {
  const qtyMillis = Math.round(qty * 1000)
  const shareMillis = Math.round(share * 1000)
  return Math.round((qtyMillis * shareMillis) / 1000) / 1000
}

/**
 * A quantity where 0 is a real answer — a count, a correction, a safety buffer, a received
 * delivery quantity: "0", "0,0" and "0.000" read as 0, everything else as {@link parseStockQuantity}.
 */
export function parseStockAmount(input: string): StockQuantityParse {
  if (/^0+(?:[.,]0+)?$/.test(input.trim())) return { ok: true, value: 0 }
  return parseStockQuantity(input)
}

/** A quantity in integer thousandths; stock quantities carry at most 3 decimals. */
function toMillis(value: number | null | undefined): number {
  return Math.round((value ?? 0) * 1000)
}

/**
 * The sum of stock quantities without binary float noise: computed in integer thousandths, so
 * 0.1 + 0.2 gives 0.3 and 1.3 + 0.2 gives 1.5. Whole numbers add exactly as they always did.
 * Null and undefined count as 0.
 */
export function addStockQuantities(
  ...values: Array<number | null | undefined>
): number {
  let millis = 0
  for (const value of values) millis += toMillis(value)
  return millis / 1000
}

/** `a − b` in integer thousandths (see {@link addStockQuantities}). */
export function subtractStockQuantities(
  a: number | null | undefined,
  b: number | null | undefined,
): number {
  return (toMillis(a) - toMillis(b)) / 1000
}

const formatter = new Intl.NumberFormat('de-AT', {
  minimumFractionDigits: 0,
  maximumFractionDigits: STOCK_DECIMALS,
  useGrouping: false,
})

/** "0,5", "12", "0,033", "-2" — de-AT comma, no trailing zeros, no thousands separator. */
export function formatStockQuantity(value: number): string {
  return formatter.format(value)
}

/**
 * A quantity on a screen that has always shown whole numbers: a whole value renders exactly as
 * `String(value)` did before decimals existed ("12", "-3", "0"), a decimal with the de-AT comma
 * ("0,5"). Also the starting text of an input that holds a stock quantity.
 */
export function formatStockDisplay(value: number): string {
  return Number.isInteger(value) ? String(value) : formatStockQuantity(value)
}

/** The share as a manager reads it: ½, ¼, ¾ for the usual portions, else the number. */
export function formatShare(share: number): string {
  if (share === 0.5) return '½'
  if (share === 0.25) return '¼'
  if (share === 0.75) return '¾'
  return formatStockQuantity(share)
}

export type PriceParse =
  | { ok: true; cents: number }
  | { ok: false; error: 'invalid' }

const PRICE_PATTERN = /^(\d+)(?:[.,](\d{1,2}))?$/

/**
 * Parses a euro amount typed as "1,50", "1.5", "2" or "2 €" into cents. Refuses negatives,
 * garbage and more than 2 decimals.
 */
export function parseEuroToCents(input: string): PriceParse {
  const trimmed = input.replace('€', '').trim()
  const match = PRICE_PATTERN.exec(trimmed)
  if (!match) return { ok: false, error: 'invalid' }
  const [, euros, cents = ''] = match
  if (euros.length > 7) return { ok: false, error: 'invalid' }
  return { ok: true, cents: Number(euros) * 100 + Number(cents.padEnd(2, '0')) }
}
